import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DmsPage, DmsAltBox, DmsModal, DmsButton, fetchData, HTTP_STATUS, intl } from 'dmsbasereact';
import { Typography, Box, Button } from '@mui/material';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import { CheckinCheckoutForm } from '../../components/CheckinCheckoutForm/CheckinCheckoutForm';
import { InvoiceDocumentTable } from '../../components/InvoiceDocumentTable/InvoiceDocumentTable';
import { InvoiceDocumentDetailModal } from '../../components/InvoiceDocumentTable/InvoiceDocumentDetailModal';
import { button3DSx } from '../../constants/ButtonStyles';

export const CheckoutPage = () => {
    const [organizationBranches, setOrganizationBranches] = useState([]);
    const [batches, setBatches] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalKey, setModalKey] = useState(0);
    const [tableKey, setTableKey] = useState(0);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const submitRef = useRef(null);

    useEffect(() => {
        const fetchInitialData = async () => {
            setIsLoading(true);
            try {
                const [branchesResponse, batchesResponse] = await Promise.all([
                    fetchData('/institution/approved-branches', 'GET', true),
                    fetchData('/batch/all', 'GET', true)
                ]);

                if (branchesResponse.status === HTTP_STATUS.OK_200) {
                    setOrganizationBranches(branchesResponse.data.data || []);
                }

                if (batchesResponse.status === HTTP_STATUS.OK_200) {
                    setBatches(batchesResponse.data.data || []);
                }
            } catch (error) {
                console.error("Failed to fetch initial data", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchInitialData();
    }, []);

    const handleOpenModal = useCallback(() => {
        setModalKey(prev => prev + 1);
        setModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setModalOpen(false);
    }, []);

    const handleCheckoutSubmitted = useCallback(() => {
        setModalOpen(false);
        setTableKey(prev => prev + 1);
    }, []);

    const handleViewDetails = useCallback((row) => {
        setSelectedInvoice(row);
        setDetailModalOpen(true);
    }, []);

    const handleCloseDetailModal = useCallback(() => {
        setDetailModalOpen(false);
        setSelectedInvoice(null);
    }, []);

    return (
        <>
            <DmsPage name={intl.get("Checkout.PageTitle").d("Exit")}>
                <DmsAltBox.Card>
                    <DmsAltBox.Header>
                        <Typography variant='h3' sx={{ fontSize: '1.1rem' }}>
                            {intl.get("Checkout.PageTitle").d("Exit")}
                        </Typography>
                    </DmsAltBox.Header>
                    <DmsAltBox.Body sx={{ overflow: 'auto' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<NoteAddOutlinedIcon sx={{ fontSize: '18px !important' }} />}
                                onClick={handleOpenModal}
                                sx={button3DSx}
                            >
                                {intl.get('Checkout.NewExit').d('New Exit')}
                            </Button>
                        </Box>

                        <InvoiceDocumentTable
                            key={tableKey}
                            url="/invoice_document/table?my_institutions=true&direction=outbound"
                            onViewDetails={handleViewDetails}
                            readOnlyLabel=""
                        />
                    </DmsAltBox.Body>
                </DmsAltBox.Card>
            </DmsPage>

            <DmsModal
                key={modalKey}
                isVisible={modalOpen}
                onClose={handleCloseModal}
                maxWidth="md"
            >
                <Box sx={{ p: 2 }}>
                    <Typography variant="h6" fontWeight={600}>
                        {intl.get('Checkout.NewExit').d('New Exit')}
                    </Typography>
                </Box>
                <DmsModal.Body>
                    <Box sx={{ p: 2.5 }}>
                        <CheckinCheckoutForm
                            organizationBranches={organizationBranches}
                            batches={batches}
                            mode="checkout"
                            hideButton
                            submitRef={submitRef}
                            onCheckoutSubmitted={handleCheckoutSubmitted}
                        />
                    </Box>
                </DmsModal.Body>
                <DmsModal.Footer>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2, width: '100%' }}>
                        <DmsButton
                            text={intl.get('Cancel').d('Cancel')}
                            onClick={handleCloseModal}
                        />
                        <DmsButton
                            text={submitRef.current?.isSubmitting
                                ? intl.get('Processing...').d('Processing...')
                                : intl.get('SubmitCheckOut').d('Submit Check-out')
                            }
                            disabled={submitRef.current?.isSubmitting}
                            variant="primary"
                            onClick={() => submitRef.current?.handleSubmit()}
                        />
                    </Box>
                </DmsModal.Footer>
            </DmsModal>

            <InvoiceDocumentDetailModal
                open={detailModalOpen}
                onClose={handleCloseDetailModal}
                invoice={selectedInvoice}
            />
        </>
    );
};
